/**
 * Cliente HTTP de la API. Token de acceso en memoria (jamás en localStorage); refresh por
 * cookie httpOnly. Ante 401 intenta UN refresh y reintenta; si falla, cierra sesión.
 */
import type { ErrorAPI } from '@mg/shared';

export class ErrorApi extends Error {
  override readonly name = 'ErrorApi';
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalles?: unknown,
  ) {
    super(mensaje);
  }
}

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';

let tokenAcceso: string | null = null;
let refrescando: Promise<string | null> | null = null;
let alExpirar: (() => void) | null = null;
let alRenovar: (() => void) | null = null;

export function setToken(token: string | null) {
  tokenAcceso = token;
}
export function getToken() {
  return tokenAcceso;
}
export function onSesionExpirada(fn: (() => void) | null) {
  alExpirar = fn;
}
/** Aviso tras renovar el token: el contexto de sucursal del superadmin puede haber vencido (ADR-0003). */
export function onTokenRenovado(fn: (() => void) | null) {
  alRenovar = fn;
}

async function leerError(res: Response): Promise<ErrorApi> {
  let cuerpo: ErrorAPI | null = null;
  try {
    cuerpo = (await res.json()) as ErrorAPI;
  } catch {
    /* sin cuerpo */
  }
  return new ErrorApi(res.status, cuerpo?.error.codigo ?? 'http_' + res.status, cuerpo?.error.mensaje ?? `Error ${res.status}`, cuerpo?.error.detalles);
}

/** Renueva el token con la cookie de refresh. Comparte una sola promesa entre llamadas concurrentes. */
export function refrescarToken(): Promise<string | null> {
  if (!refrescando) {
    refrescando = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (!res.ok) return null;
        const datos = (await res.json()) as { token: string };
        tokenAcceso = datos.token;
        queueMicrotask(() => alRenovar?.());
        return datos.token;
      } catch {
        return null;
      } finally {
        refrescando = null;
      }
    })();
  }
  return refrescando;
}

export interface OpcionesApi {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  /** No intentar refresh al 401 (login, refresh). */
  sinRefresh?: boolean;
}

export async function api<T>(metodo: string, ruta: string, opciones: OpcionesApi = {}): Promise<T> {
  const url = new URL(`${BASE}${ruta}`, window.location.origin);
  for (const [k, v] of Object.entries(opciones.query ?? {})) if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
  const hacer = async (token: string | null) => {
    const headers: Record<string, string> = { accept: 'application/json', ...opciones.headers };
    if (opciones.body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers['authorization'] = `Bearer ${token}`;
    return fetch(url, { method: metodo, headers, credentials: 'include', body: opciones.body === undefined ? null : JSON.stringify(opciones.body) });
  };
  let res = await hacer(tokenAcceso);
  if (res.status === 401 && !opciones.sinRefresh) {
    const nuevo = await refrescarToken();
    if (nuevo) res = await hacer(nuevo);
    else {
      tokenAcceso = null;
      alExpirar?.();
      throw await leerError(res);
    }
  }
  if (!res.ok) throw await leerError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const get = <T>(ruta: string, query?: OpcionesApi['query']) => api<T>('GET', ruta, query ? { query } : {});
/** Clave de operación nueva (cabecera idempotency-key): la misma mientras se reintenta, otra al terminar. */
export const nuevaClaveOperacion = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
export const post = <T>(ruta: string, body?: unknown, opciones: OpcionesApi = {}) => api<T>('POST', ruta, { ...opciones, body });
export const patch = <T>(ruta: string, body?: unknown) => api<T>('PATCH', ruta, { body });
export const put = <T>(ruta: string, body?: unknown) => api<T>('PUT', ruta, { body });
export const del = <T>(ruta: string) => api<T>('DELETE', ruta);

/** Nombre de los datos más comunes en los mensajes de validación; el resto se muestra sin guiones bajos. */
const CAMPO: Record<string, string> = {
  cantidad: 'cantidad', unidades: 'unidades', precio_divisa: 'precio', precio: 'precio', costo_unitario: 'costo', monto: 'monto', metodo: 'método',
  referencia: 'referencia', rif: 'RIF', email: 'correo', correo: 'correo', password: 'contraseña', nombre: 'nombre', razon_social: 'razón social',
  direccion_fiscal: 'dirección fiscal', direccion: 'dirección', telefono: 'teléfono', codigo: 'código', motivo: 'motivo', justificacion: 'justificación',
  observacion: 'observación', comentario: 'comentario', descripcion: 'descripción', detalle: 'detalle', fecha: 'fecha', desde: 'desde', hasta: 'hasta',
  bcv: 'tasa BCV', paralela: 'tasa 2', tasa_bcv: 'tasa BCV', fondo_usd: 'fondo $', fondo_bs: 'fondo Bs', limite_credito_usd: 'límite de crédito',
  plazo_dias: 'plazo (días)', numero_control: 'número de control', control: 'número de control', numero_factura: 'número de factura', serie: 'serie',
  valor: 'valor', minimo: 'mínimo', fisico: 'físico', cedula: 'cédula', producto_id: 'producto', presentacion_id: 'presentación', cliente_id: 'cliente',
  almacen_id: 'almacén', sucursal_id: 'sucursal', proveedor_id: 'proveedor', rol: 'rol', lineas: 'renglones', pagos: 'pagos', piezas: 'piezas', permisos: 'permisos',
};
/** Listas de la entrada: el índice se cuenta desde 1 («Renglón 1»), como se ven en la pantalla. */
const LISTA: Record<string, string> = { lineas: 'Renglón', pagos: 'Pago', piezas: 'Pieza', permisos: 'Permiso', conteo: 'Línea del conteo', lotes_punto: 'Lote del punto', costos: 'Costo' };

/**
 * Rótulo de un dato inválido sin el camino interno: «lineas.0.cantidad» → «Renglón 1 · cantidad» (re-auditoría
 * 2026-10-18, ronda 2, hallazgos 3-H16 y 7-H13). Si el mensaje ya nombra el dato, no se repite.
 */
function rotuloCampo(campo: string, mensaje: string): string {
  const partes = campo.split('.').filter((p) => p !== '');
  const salida: string[] = [];
  partes.forEach((p, i) => {
    if (/^\d+$/.test(p)) {
      const lista = partes[i - 1];
      salida.push(`${(lista && LISTA[lista]) ?? 'Renglón'} ${Number(p) + 1}`);
    } else if (i === partes.length - 1) {
      const nombre = CAMPO[p] ?? p.replace(/_/g, ' ');
      if (!mensaje.toLowerCase().includes(nombre.toLowerCase())) salida.push(nombre);
    }
  });
  const rotulo = salida.join(' · ');
  return rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
}

/** Mensaje amigable para toasts/estados de error. */
export function mensajeDeError(e: unknown): string {
  if (e instanceof ErrorApi) {
    if (e.codigo === 'validacion' && Array.isArray(e.detalles)) {
      return (e.detalles as { campo: string; mensaje: string }[])
        .map((d) => {
          const rotulo = rotuloCampo(d.campo ?? '', d.mensaje);
          return rotulo ? `${rotulo}: ${d.mensaje}` : d.mensaje;
        })
        .join('; ');
    }
    return e.message;
  }
  if (e instanceof Error) return e.message === 'Failed to fetch' ? 'No se pudo conectar con el servidor' : e.message;
  return 'Ocurrió un error inesperado';
}
